import React from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import { BRAND } from "../../constants/brand";

type AppLogoSize = "sm" | "md" | "lg" | "xl";

const LOGO_PX: Record<AppLogoSize, number> = {
  sm: 24,
  md: 36,
  lg: 48,
  xl: 96,
};

const LOGO_SRC: Record<AppLogoSize, string> = {
  sm: BRAND.logo.sm,
  md: BRAND.logo.md,
  lg: BRAND.logo.lg,
  xl: BRAND.logo.xl,
};

export interface AppLogoProps {
  size?: AppLogoSize;
  showText?: boolean;
  /** Product line under logo; defaults to BRAND.name */
  title?: string;
  subtitle?: string;
  /** Light text on dark / orange panels */
  inverted?: boolean;
  /** White rounded plate — hides logo’s built-in white square on colored backgrounds */
  withPlate?: boolean;
  sx?: SxProps<Theme>;
}

const AppLogo: React.FC<AppLogoProps> = ({
  size = "sm",
  showText = false,
  title,
  subtitle,
  inverted = false,
  withPlate = false,
  sx,
}) => {
  const px = LOGO_PX[size];
  const platePad = size === "xl" ? 2.5 : size === "lg" ? 2 : 1.25;

  const logoImg = (
    <Box
      component="img"
      src={LOGO_SRC[size]}
      alt={`${BRAND.name} logo`}
      width={px}
      height={px}
      sx={{
        width: px,
        height: px,
        flexShrink: 0,
        objectFit: "contain",
        display: "block",
      }}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        ...sx,
      }}
    >
      {withPlate ? (
        <Box
          sx={{
            bgcolor: "common.white",
            borderRadius: size === "xl" ? 3 : 2,
            p: platePad,
            display: "inline-flex",
            flexShrink: 0,
            boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
          }}
        >
          {logoImg}
        </Box>
      ) : (
        logoImg
      )}
      {showText && (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              lineHeight: 1.2,
              color: inverted ? "common.white" : "text.primary",
            }}
            noWrap
          >
            {title ?? BRAND.name}
          </Typography>
          {(subtitle || BRAND.tagline) && (
            <Typography
              variant="caption"
              sx={{
                color: inverted ? "rgba(255,255,255,0.85)" : "text.secondary",
                fontSize: "0.625rem",
                display: "block",
              }}
              noWrap
            >
              {subtitle ?? BRAND.tagline}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AppLogo;
